import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ShieldCheck, ClipboardList, UserCog, CreditCard, CheckCircle2, Check,
  ArrowRight, ArrowLeft, Loader2, AlertCircle, Tag, Phone, Lock, Building2,
} from 'lucide-react';
import onboardingService from '../api/onboarding.service';
import './Adquirir.css';
import './ajustes-portadas.css';

/**
 * Registro con pago: el «sign up» de una residencial.
 *
 * Mismo recorrido y mismo estilo que el de MediClinic Pro, con los pasos de
 * este producto. La residencial NO se crea al llenar el formulario: se crea
 * cuando Wompi confirma el pago, y esa confirmación va firmada. Hasta
 * entonces solo existe una compra pendiente.
 *
 * Lo que sostiene todo esto es que el recorrido SOBREVIVE al viaje a la
 * pasarela: el navegador se va a Wompi y vuelve con una URL nueva, así que
 * los datos se guardan en `sessionStorage` y la referencia viaja en la URL.
 * Sin eso, quien pagara volvería a un formulario en blanco.
 */

const PASOS = [
  { id: 1, label: 'Residencial', icon: ClipboardList },
  { id: 2, label: 'Administrador', icon: UserCog },
  { id: 3, label: 'Pago', icon: CreditCard },
  { id: 4, label: 'Listo', icon: CheckCircle2 },
];

const PASO_PAGO = 3;
const PASO_LISTO = 4;

const WHATSAPP =
  'https://wa.me/50368960231?text=Hola%2C%20tengo%20una%20duda%20sobre%20la%20suscripci%C3%B3n%20de%20mi%20residencial';

/** Lee un valor guardado; sobrevive al ir y volver de la pasarela. */
const leer = (clave, porDefecto) => {
  try {
    const crudo = sessionStorage.getItem(`adquirir_${clave}`);
    return crudo ? JSON.parse(crudo) : porDefecto;
  } catch {
    return porDefecto;
  }
};

const guardar = (clave, valor) => {
  try {
    sessionStorage.setItem(`adquirir_${clave}`, JSON.stringify(valor));
  } catch {
    // Almacenamiento lleno o bloqueado: el registro sigue en memoria.
  }
};

const limpiar = () => {
  try {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith('adquirir_'))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* nada que limpiar */
  }
};

/**
 * Campo con su etiqueta.
 *
 * Declarado FUERA del componente a propósito: definido dentro, React lo vería
 * como un tipo nuevo en cada render, desmontaría el campo y se perdería el
 * foco a cada letra que se escribe.
 */
const Campo = ({ label, requerido, children, ancho }) => (
  <label className={`field-adquirir${ancho ? ' field-wide-adquirir' : ''}`}>
    <span className="label-adquirir">
      {label}
      {requerido && <em className="required-adquirir" aria-hidden="true"> *</em>}
    </span>
    {children}
  </label>
);

function Adquirir() {
  const [params, setParams] = useSearchParams();
  const referenciaUrl = params.get('reference');

  const [paso, setPaso] = useState(() => (referenciaUrl ? PASO_PAGO : leer('paso', 1)));
  const [planes, setPlanes] = useState([]);
  const [pagosActivos, setPagosActivos] = useState(true);
  const [plan, setPlan] = useState(() => leer('plan', 'basic_monthly'));
  const [anual, setAnual] = useState(() => leer('anual', false));

  const [residencial, setResidencial] = useState(() =>
    leer('residencial', { name: '', phone: '', email: '', address: '', polygons: '', houses: '' })
  );
  const [admin, setAdmin] = useState(() => leer('admin', { name: '', email: '', phone: '' }));
  const [clave, setClave] = useState('');
  const [claveRepetida, setClaveRepetida] = useState('');

  const [cupon, setCupon] = useState(() => leer('cupon', ''));
  const [cuponAplicado, setCuponAplicado] = useState(null);
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(false);
  const [compra, setCompra] = useState(() => leer('compra', null));
  const [estado, setEstado] = useState(null);
  const sondeo = useRef(null);

  // ── Planes ──
  useEffect(() => {
    onboardingService
      .getPlans()
      .then((res) => {
        setPlanes(res.data || []);
        setPagosActivos(res.paymentsEnabled !== false);
      })
      .catch(() => setPlanes([]));
  }, []);

  useEffect(() => guardar('paso', paso), [paso]);
  useEffect(() => guardar('plan', plan), [plan]);
  useEffect(() => guardar('anual', anual), [anual]);
  useEffect(() => guardar('residencial', residencial), [residencial]);
  useEffect(() => guardar('admin', admin), [admin]);
  useEffect(() => guardar('cupon', cupon), [cupon]);
  useEffect(() => guardar('compra', compra), [compra]);

  const planSeleccionado = planes.find((p) => p.id === plan);
  const nivel = plan.startsWith('custom') ? 'custom' : 'basic';

  // Al cambiar mensual/anual se conserva el nivel elegido.
  const cambiarPeriodo = (esAnual) => {
    setAnual(esAnual);
    setPlan(`${nivel}_${esAnual ? 'annual' : 'monthly'}`);
    setCuponAplicado(null);
  };

  const elegirNivel = (nuevoNivel) => {
    setPlan(`${nuevoNivel}_${anual ? 'annual' : 'monthly'}`);
    setCuponAplicado(null);
  };

  /**
   * Al volver de la pasarela.
   *
   * Se reenvían TODOS los parámetros que puso Wompi: el servidor recalcula el
   * hash con ellos y, si cuadra, crea la residencial sin esperar al webhook.
   * Si no hay hash, se consulta el estado y se sondea: el webhook puede tardar
   * unos segundos.
   */
  const verificarRegreso = useCallback(async (referencia) => {
    setCargando(true);
    try {
      const todos = Object.fromEntries(new URLSearchParams(window.location.search));
      const res = await onboardingService.verificarRegreso({ ...todos, reference: referencia });
      setEstado(res.data);
      if (res.data?.status === 'provisioned') {
        setPaso(PASO_LISTO);
        limpiar();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo confirmar el pago.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    if (!referenciaUrl) return undefined;
    verificarRegreso(referenciaUrl);

    // Sondeo mientras el pago se confirma. Se detiene solo al lograrlo.
    sondeo.current = setInterval(async () => {
      try {
        const res = await onboardingService.estado(referenciaUrl);
        setEstado(res.data);
        if (res.data?.status === 'provisioned') {
          clearInterval(sondeo.current);
          setPaso(PASO_LISTO);
          limpiar();
        }
      } catch {
        /* se reintenta en el siguiente ciclo */
      }
    }, 5000);

    return () => clearInterval(sondeo.current);
  }, [referenciaUrl, verificarRegreso]);

  // ── Validaciones por paso ──
  const validarPaso = () => {
    if (paso === 1) {
      if (!residencial.name.trim() || residencial.name.trim().length < 3) {
        return 'Escriba el nombre de la residencial.';
      }
    }
    if (paso === 2) {
      if (!admin.name.trim()) return 'Escriba el nombre del administrador.';
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(admin.email)) return 'Escriba un correo válido.';
      if (clave.length < 6) return 'La contraseña debe tener al menos 6 caracteres.';
      if (!/(?=.*[A-Za-z])(?=.*\d)/.test(clave)) return 'La contraseña debe llevar letras y números.';
      if (clave !== claveRepetida) return 'Las dos contraseñas no coinciden.';
    }
    return '';
  };

  const siguiente = () => {
    const fallo = validarPaso();
    if (fallo) {
      setError(fallo);
      return;
    }
    setError('');
    setPaso((p) => Math.min(p + 1, PASO_LISTO));
  };

  const anterior = () => {
    setError('');
    setPaso((p) => Math.max(p - 1, 1));
  };

  const aplicarCupon = async () => {
    if (!cupon.trim()) return;
    try {
      const res = await onboardingService.validarCupon(cupon.trim(), plan);
      setCuponAplicado(res.data);
      setError('');
    } catch (err) {
      setCuponAplicado(null);
      setError(err.response?.data?.error || 'Cupón inválido.');
    }
  };

  const pagar = async () => {
    setCargando(true);
    setError('');
    try {
      const res = await onboardingService.iniciar({
        residentialData: {
          name: residencial.name.trim(),
          phone: residencial.phone,
          email: residencial.email,
          address: residencial.address,
          houses: residencial.houses ? Number(residencial.houses) : undefined,
          polygons: residencial.polygons
            ? residencial.polygons.split(',').map((p) => p.trim()).filter(Boolean)
            : [],
        },
        adminData: { name: admin.name.trim(), email: admin.email.trim(), phone: admin.phone },
        plan,
        password: clave,
        couponCode: cuponAplicado ? cuponAplicado.code : undefined,
      });

      setCompra(res.data);
      if (res.data.paymentUrl) {
        // Se va a la pasarela. Al volver, la referencia en la URL retoma todo.
        window.location.href = res.data.paymentUrl;
        return;
      }
      setError(
        'No se pudo generar el enlace de pago. Escríbanos por WhatsApp y lo activamos manualmente.'
      );
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo iniciar el pago.');
    } finally {
      setCargando(false);
    }
  };

  const montoFinal = cuponAplicado ? cuponAplicado.finalAmount : planSeleccionado?.price;

  // ── Pantallas ──
  const paginaListo = () => (
    <div className="done-adquirir">
      <span className="done-icon-adquirir" aria-hidden="true"><CheckCircle2 /></span>
      <h2 className="step-title-adquirir">Su residencial está lista</h2>
      <p className="done-text-adquirir">
        Creamos <strong>{estado?.residentialName || residencial.name}</strong> y la cuenta de su
        administrador. Ya puede iniciar sesión con{' '}
        <strong>{estado?.adminEmail || admin.email}</strong> y la contraseña que eligió.
      </p>
      <p className="done-hint-adquirir">
        Desde ahí dé de alta a sus guardias y residentes. Si necesita ayuda, escríbanos.
      </p>
      <div className="actions-adquirir">
        <Link to="/login" className="btn-adquirir btn-primary-adquirir btn-lg-adquirir">
          Iniciar sesión <ArrowRight />
        </Link>
        <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="btn-adquirir btn-outline-adquirir">
          <Phone /> Escribirnos
        </a>
      </div>
      {estado?.reference && (
        <p className="reference-adquirir">Referencia de su pago: {estado.reference}</p>
      )}
    </div>
  );

  const paginaPago = () => (
    <>
      <div className="step-head-adquirir">
        <h2 className="step-title-adquirir">Elija su plan y pague</h2>
        <p className="step-text-adquirir">
          El pago se procesa en Wompi. Nosotros no vemos ni guardamos los datos de su tarjeta.
        </p>
      </div>

      {referenciaUrl && estado?.status !== 'provisioned' && (
        <div className="alert-adquirir alert-info-adquirir">
          <Loader2 className="spin-adquirir" />
          <span>
            Estamos confirmando su pago con el banco. Esto puede tardar unos segundos; no cierre
            esta página.
          </span>
        </div>
      )}

      <div className="switch-adquirir">
        <span className="switch-text-adquirir">Mensual</span>
        <button
          type="button"
          className={`switch-track-adquirir${anual ? ' is-active-adquirir' : ''}`}
          onClick={() => cambiarPeriodo(!anual)}
          aria-pressed={anual}
          aria-label="Cambiar entre pago mensual y anual"
        />
        <span className="switch-text-adquirir">Anual · 2 meses gratis</span>
      </div>

      <div className="plans-adquirir">
        {['basic', 'custom'].map((n) => {
          const p = planes.find((x) => x.tier === n && x.interval === (anual ? 'anual' : 'mensual'));
          const elegido = nivel === n;
          return (
            <button
              type="button"
              key={n}
              className={`plan-adquirir${elegido ? ' is-selected-adquirir' : ''}`}
              onClick={() => elegirNivel(n)}
              aria-pressed={elegido}
            >
              <span className="plan-check-adquirir" aria-hidden="true">{elegido && <Check />}</span>
              <span className="plan-label-adquirir">{n === 'basic' ? 'Base' : 'Completo'}</span>
              <span className="plan-price-adquirir">
                ${p ? p.price.toLocaleString('es-SV') : '—'}
                <small>/{anual ? 'año' : 'mes'}</small>
              </span>
              {/* Lista propia: la del resumen lateral vive sobre fondo oscuro
                  y su texto es blanco. Aqui el fondo de la tarjeta es claro. */}
              <ul className="plan-list-adquirir">
                {(p?.features || []).slice(0, 5).map((f) => (
                  <li key={f}><Check /> {f}</li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>

      <div className="coupon-row-adquirir">
        <Campo label="¿Tiene un cupón?">
          <input
            className="input-adquirir"
            value={cupon}
            onChange={(e) => setCupon(e.target.value.toUpperCase())}
            placeholder="CUPON2026"
          />
        </Campo>
        <button type="button" className="btn-adquirir btn-outline-adquirir btn-sm-adquirir" onClick={aplicarCupon}>
          <Tag /> Aplicar
        </button>
      </div>

      {cuponAplicado && (
        <p className="field-success-adquirir">
          Cupón {cuponAplicado.code} aplicado: {cuponAplicado.label}
        </p>
      )}

      <div className="summary-adquirir">
        <div>
          <span>{planSeleccionado?.label || 'Plan'}</span>
          <span>${planSeleccionado ? planSeleccionado.price.toLocaleString('es-SV') : '—'}</span>
        </div>
        {cuponAplicado && (
          <div className="summary-discount-adquirir">
            <span>Descuento</span>
            <span>
              −${(planSeleccionado.price - cuponAplicado.finalAmount).toLocaleString('es-SV')}
            </span>
          </div>
        )}
        <div className="summary-total-adquirir">
          <span>Total a pagar hoy</span>
          <span>${montoFinal ? montoFinal.toLocaleString('es-SV') : '—'}</span>
        </div>
      </div>

      {!pagosActivos && (
        <div className="alert-adquirir alert-warning-adquirir">
          <AlertCircle />
          <span>
            El pago en línea no está disponible en este momento. Escríbanos por WhatsApp y
            activamos su residencial.
          </span>
        </div>
      )}

      <div className="actions-adquirir">
        <button type="button" className="btn-adquirir btn-ghost-adquirir" onClick={anterior}>
          <ArrowLeft /> Atrás
        </button>
        <button
          type="button"
          className="btn-adquirir btn-primary-adquirir btn-lg-adquirir"
          onClick={pagar}
          disabled={cargando || !pagosActivos}
        >
          {cargando ? <Loader2 className="spin-adquirir" /> : <Lock />}
          {cargando ? 'Preparando el pago…' : 'Pagar y activar'}
        </button>
      </div>

      <p className="note-adquirir">
        <Lock /> Pago procesado por Wompi El Salvador. Su residencial se crea cuando el banco
        confirma el cobro.
      </p>
    </>
  );

  return (
    <div className="page-adquirir">
      <header className="header-adquirir">
        <div className="container-adquirir header-inner-adquirir">
          <Link to="/" className="brand-adquirir">
            <ShieldCheck strokeWidth={2.25} aria-hidden="true" />
            <span>Control Residencial</span>
          </Link>
          <Link to="/login" className="btn-adquirir btn-outline-adquirir btn-sm-adquirir">
            Ya tengo cuenta
          </Link>
        </div>
      </header>

      <div className="band-adquirir">
        <img
          className="band-photo-adquirir"
          src="https://images.unsplash.com/photo-1775112077888-8fa36e9bbc51?auto=format&q=80&w=1600"
          alt=""
        />
        <div className="band-shade-adquirir" aria-hidden="true" />
        <div className="container-adquirir band-inner-adquirir">
          <div className="band-panel-adquirir">
            <p className="band-label-adquirir">Registro</p>
            <h1 className="band-title-adquirir">Active el control de acceso de su residencial</h1>
            <p className="band-intro-adquirir">
              Lo que escriba se guarda en este navegador: puede ir al pago y volver sin perder nada.
            </p>
          </div>
        </div>

        {/* La barra de progreso va DENTRO de la banda, sobre la foto.
            Estaba dentro de la rejilla de abajo, y como esa rejilla tiene dos
            columnas —formulario y resumen— la barra ocupaba la primera celda y
            empujaba el formulario a la columna angosta de 320 px. Se veía
            aplastado contra el borde, con el resumen ocupando todo el ancho. */}
        <nav className="stepper-adquirir" aria-label="Progreso">
          <ol>
            {PASOS.map((s) => (
              <li
                key={s.id}
                className={`stepper-item-adquirir${paso === s.id ? ' is-active-adquirir' : ''}${
                  paso > s.id ? ' is-done-adquirir' : ''
                }`}
                aria-current={paso === s.id ? 'step' : undefined}
              >
                <span className="stepper-circle-adquirir" aria-hidden="true">
                  {paso > s.id ? <Check strokeWidth={3} /> : <s.icon />}
                </span>
                <span className="stepper-label-adquirir">{s.label}</span>
              </li>
            ))}
          </ol>
          <p className="stepper-mobile-adquirir" aria-hidden="true">
            Paso {paso} de {PASOS.length} · {PASOS[paso - 1]?.label}
          </p>
        </nav>
      </div>

      <main className="container-adquirir main-adquirir">
        <section className="card-adquirir">
          {error && (
            <div className="alert-adquirir alert-danger-adquirir" role="alert">
              <AlertCircle />
              <span>{error}</span>
            </div>
          )}

          {paso === 1 && (
            <>
              <div className="step-head-adquirir">
                <h2 className="step-title-adquirir">Su residencial</h2>
                <p className="step-text-adquirir">
                  Este nombre es el que verán sus residentes y, en su enlace, los visitantes.
                </p>
              </div>
              <div className="grid-adquirir">
                <Campo label="Nombre de la residencial" requerido ancho>
                  <input
                    className="input-adquirir"
                    value={residencial.name}
                    onChange={(e) => setResidencial({ ...residencial, name: e.target.value })}
                    /* Un ejemplo genérico, no el nombre de una residencial que
                       existe en la plataforma: este formulario lo ve un
                       prospecto, y ahí el nombre de un cliente no pinta nada. */
                    placeholder="Residencial Los Pinos"
                    autoComplete="organization"
                  />
                </Campo>
                <Campo label="Teléfono">
                  <input
                    className="input-adquirir"
                    value={residencial.phone}
                    onChange={(e) => setResidencial({ ...residencial, phone: e.target.value })}
                    placeholder="2222-3333"
                    autoComplete="tel"
                  />
                </Campo>
                <Campo label="Correo de la administración">
                  <input
                    className="input-adquirir"
                    type="email"
                    value={residencial.email}
                    onChange={(e) => setResidencial({ ...residencial, email: e.target.value })}
                    placeholder="administracion@suresidencial.com"
                  />
                </Campo>
                <Campo label="Dirección" ancho>
                  <input
                    className="input-adquirir"
                    value={residencial.address}
                    onChange={(e) => setResidencial({ ...residencial, address: e.target.value })}
                    placeholder="Calle principal, San Salvador"
                  />
                </Campo>
                <Campo label="Polígonos o etapas">
                  <input
                    className="input-adquirir"
                    value={residencial.polygons}
                    onChange={(e) => setResidencial({ ...residencial, polygons: e.target.value })}
                    placeholder="A, B, C"
                  />
                </Campo>
                <Campo label="Cuántas casas">
                  <input
                    className="input-adquirir"
                    type="number"
                    min="0"
                    value={residencial.houses}
                    onChange={(e) => setResidencial({ ...residencial, houses: e.target.value })}
                    placeholder="120"
                  />
                </Campo>
              </div>
              <div className="actions-adquirir">
                <Link to="/" className="btn-adquirir btn-ghost-adquirir">
                  <ArrowLeft /> Volver
                </Link>
                <button type="button" className="btn-adquirir btn-primary-adquirir" onClick={siguiente}>
                  Continuar <ArrowRight />
                </button>
              </div>
            </>
          )}

          {paso === 2 && (
            <>
              <div className="step-head-adquirir">
                <h2 className="step-title-adquirir">Su cuenta de administrador</h2>
                <p className="step-text-adquirir">
                  Con esta cuenta dará de alta a los guardias y a los residentes.
                </p>
              </div>
              <div className="grid-adquirir">
                <Campo label="Nombre completo" requerido ancho>
                  <input
                    className="input-adquirir"
                    value={admin.name}
                    onChange={(e) => setAdmin({ ...admin, name: e.target.value })}
                    autoComplete="name"
                  />
                </Campo>
                <Campo label="Correo" requerido>
                  <input
                    className="input-adquirir"
                    type="email"
                    value={admin.email}
                    onChange={(e) => setAdmin({ ...admin, email: e.target.value })}
                    autoComplete="email"
                  />
                </Campo>
                <Campo label="Teléfono">
                  <input
                    className="input-adquirir"
                    value={admin.phone}
                    onChange={(e) => setAdmin({ ...admin, phone: e.target.value })}
                    autoComplete="tel"
                  />
                </Campo>
                <Campo label="Contraseña" requerido>
                  <input
                    className="input-adquirir"
                    type="password"
                    value={clave}
                    onChange={(e) => setClave(e.target.value)}
                    autoComplete="new-password"
                    placeholder="Letras y números, mínimo 6"
                  />
                </Campo>
                <Campo label="Repita la contraseña" requerido>
                  <input
                    className="input-adquirir"
                    type="password"
                    value={claveRepetida}
                    onChange={(e) => setClaveRepetida(e.target.value)}
                    autoComplete="new-password"
                  />
                </Campo>
              </div>
              <div className="actions-adquirir">
                <button type="button" className="btn-adquirir btn-ghost-adquirir" onClick={anterior}>
                  <ArrowLeft /> Atrás
                </button>
                <button type="button" className="btn-adquirir btn-primary-adquirir" onClick={siguiente}>
                  Continuar <ArrowRight />
                </button>
              </div>
            </>
          )}

          {paso === PASO_PAGO && paginaPago()}
          {paso === PASO_LISTO && paginaListo()}
        </section>

        <aside className="aside-adquirir">
          {/* `aside-plan-adquirir` es la tarjeta OSCURA. Sin ella, todo este
              bloque -que es de texto blanco- quedaba sobre el fondo claro de
              la pagina: invisible. */}
          <div className="aside-plan-adquirir">
            <p className="aside-eyebrow-adquirir">Su plan</p>
            <p className="aside-plan-name-adquirir">
              <Building2 aria-hidden="true" /> {nivel === 'basic' ? 'Base' : 'Completo'}
            </p>
            <p className="aside-plan-price-adquirir">
              ${planSeleccionado ? planSeleccionado.price.toLocaleString('es-SV') : '—'}
              <small>/{anual ? 'año' : 'mes'}</small>
            </p>
            <ul className="aside-list-adquirir">
              {(planSeleccionado?.features || []).map((f) => (
                <li key={f}><Check /> {f}</li>
              ))}
            </ul>
            <p className="aside-note-adquirir">
              Sin contratos a largo plazo. Puede cancelar cuando quiera.
            </p>
          </div>
          <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="aside-help-adquirir">
            <span className="aside-help-icon-adquirir" aria-hidden="true"><Phone /></span>
            ¿Dudas? Escríbanos
          </a>
        </aside>
      </main>
    </div>
  );
}

export default Adquirir;
